import React, { useState, useEffect } from 'react';
import { Save, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import SEOSnippetPreview from '../components/SEOSnippetPreview';

interface PageSEO {
  id?: number;
  pagePath: string;
  pageName: string;
  isPublic: boolean;
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
  seoOgTitle?: string;
  seoOgDescription?: string;
  seoOgImage?: string;
  seoCanonical?: string;
  seoMetaRobots?: string;
  landingContent?: string;
  landingCtaText?: string;
}

const PageSEOSettingsPage: React.FC = () => {
  const isPageIndexable = (page: PageSEO) => {
    const robots = (page.seoMetaRobots || '').toLowerCase();
    return page.isPublic && !robots.includes('noindex');
  };

  const handleIndexingToggle = (checked: boolean) => {
    if (!selectedPage) return;
    setSelectedPage({
      ...selectedPage,
      isPublic: checked,
      seoMetaRobots: checked ? 'index, follow' : 'noindex, nofollow'
    });
  };

  const [pages, setPages] = useState<PageSEO[]>([]);
  const [selectedPage, setSelectedPage] = useState<PageSEO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/page-seo');
      if (response.data.success) {
        setPages(response.data.pages || []);
        if (response.data.pages && response.data.pages.length > 0 && !selectedPage) {
          setSelectedPage(response.data.pages[0]);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка загрузки страниц');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPage) return;

    try {
      setSaving(true);
      const response = await api.put(`/admin/page-seo/${encodeURIComponent(selectedPage.pagePath)}`, selectedPage);
      if (response.data.success) {
        toast.success('SEO настройки сохранены');
        fetchPages();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">SEO настройки страниц</h1>
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          {showPreview ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          {showPreview ? 'Скрыть превью' : 'Показать превью'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Список страниц */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            <h2 className="text-lg font-semibold mb-4">Страницы</h2>
            <div className="space-y-2">
              {pages.map((page) => (
                <button
                  key={page.pagePath}
                  onClick={() => setSelectedPage(page)}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    selectedPage?.pagePath === page.pagePath
                      ? 'bg-purple-100 text-purple-900'
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{page.pageName}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isPageIndexable(page) ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {isPageIndexable(page) ? 'Индексируется' : 'Noindex'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">{page.pagePath}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Форма редактирования */}
        <div className="lg:col-span-2">
          {selectedPage ? (
            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">{selectedPage.pageName}</h2>
                <p className="text-sm text-gray-500">{selectedPage.pagePath}</p>
              </div>

              {/* Превью сниппетов */}
              {showPreview && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <SEOSnippetPreview
                    title={selectedPage.seoTitle || selectedPage.pageName}
                    description={selectedPage.seoDescription || ''}
                    url={`https://rabotamax.ru${selectedPage.pagePath}`}
                    searchEngine="google"
                  />
                  <SEOSnippetPreview
                    title={selectedPage.seoTitle || selectedPage.pageName}
                    description={selectedPage.seoDescription || ''}
                    url={`https://rabotamax.ru${selectedPage.pagePath}`}
                    searchEngine="yandex"
                  />
                </div>
              )}

              {/* SEO поля */}
              <div className="space-y-4">
                <div className="p-3 rounded-lg border border-gray-200 bg-gray-50">
                  <label className="flex items-center justify-between gap-4 cursor-pointer">
                    <div>
                      <div className="text-sm font-medium text-gray-800">Индексировать страницу</div>
                      <div className="text-xs text-gray-500 mt-1">
                        Управляет доступностью в индексе: `isPublic` и `seoMetaRobots`
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={isPageIndexable(selectedPage)}
                      onChange={(e) => handleIndexingToggle(e.target.checked)}
                      className="h-5 w-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SEO Title
                  </label>
                  <input
                    type="text"
                    value={selectedPage.seoTitle || ''}
                    onChange={(e) => setSelectedPage({ ...selectedPage, seoTitle: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Заголовок страницы"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SEO Description
                  </label>
                  <textarea
                    value={selectedPage.seoDescription || ''}
                    onChange={(e) => setSelectedPage({ ...selectedPage, seoDescription: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Описание страницы"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Landing Content (для незарегистрированных)
                  </label>
                  <textarea
                    value={selectedPage.landingContent || ''}
                    onChange={(e) => setSelectedPage({ ...selectedPage, landingContent: e.target.value })}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Описание раздела для незарегистрированных пользователей"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CTA Text
                  </label>
                  <input
                    type="text"
                    value={selectedPage.landingCtaText || ''}
                    onChange={(e) => setSelectedPage({ ...selectedPage, landingCtaText: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Текст призыва к действию"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                >
                  <Save className="h-5 w-5" />
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
              Выберите страницу для редактирования
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PageSEOSettingsPage;

